import { Link } from 'react-router-dom'
import { Button } from '@orisini/ui/components/button'
import { planQuotaRows } from '@orisini/ui/lib/plan-quota'
import { Bagian, GagalMuat } from './Bagian'
import { rupiah, type Paket } from '../lib/data'

function KolomPaket({ paket, tahunan }: { paket: Paket; tahunan: boolean }) {
  const harga = tahunan ? paket.priceYearly : paket.priceMonthly
  const gratis = paket.priceMonthly === 0

  return (
    <div className="flex flex-col gap-5 border-b border-border py-8 lg:border-b-0 lg:border-l lg:border-border lg:px-6 lg:first:border-l-0 lg:first:pl-0 lg:last:pr-0">
      <div>
        <h3 className="font-medium">{paket.name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{paket.description}</p>
      </div>

      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold tabular-nums tracking-tight">
            {gratis ? 'Free' : rupiah(harga)}
          </span>
          {!gratis && (
            <span className="text-sm text-muted-foreground">{tahunan ? '/year' : '/month'}</span>
          )}
        </div>

        {gratis && (
          <p className="mt-1 text-xs text-muted-foreground">
            No time limit, no credit card.
          </p>
        )}

        {tahunan && !gratis && (
          <p className="mt-1 text-xs text-muted-foreground">
            {rupiah(Math.round(paket.priceYearly / 12))} per month — save{' '}
            {rupiah(paket.priceMonthly * 12 - paket.priceYearly)}
          </p>
        )}
      </div>

      {/* Satu baris per jenis kuota — gambar, carousel, dan video masing-masing
          punya batasnya sendiri, jadi masing-masing juga punya barisnya. */}
      <dl className="divide-y divide-border border-y border-border text-sm">
        {planQuotaRows(paket).map((r) => (
          <div key={r.key} className="flex items-baseline justify-between gap-4 py-2">
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd className="tabular-nums">{r.value}</dd>
          </div>
        ))}
      </dl>

      <Button
        asChild
        variant={paket.code === 'starter' ? 'default' : 'outline'}
        className="mt-auto w-full"
      >
        <Link to="/register">{gratis ? 'Start free' : `Choose ${paket.name}`}</Link>
      </Button>
    </div>
  )
}

/**
 * Tabel harga Orisini — satu paket untuk semua platform.
 *
 * Datanya datang dari katalog paket yang sama dengan halaman langganan, jadi
 * harga di sini tidak bisa berbeda dari yang benar-benar ditagih.
 */
export function BagianHarga({
  paket,
  memuat,
  gagal,
  ulangi,
  tahunan,
  padaGantiPeriode,
}: {
  paket: Paket[]
  memuat: boolean
  gagal: boolean
  ulangi: () => void
  tahunan: boolean
  padaGantiPeriode: (tahunan: boolean) => void
}) {
  return (
    <Bagian
      id="pricing"
      judul="Free to try, Rp49.000 to get serious"
      keterangan="One plan for every platform. What's limited is how much you post and create each month, not where you post it. There is no setup fee, and the Free plan does not ask for a credit card."
    >
      {gagal ? (
        <GagalMuat pesan="Couldn't load the plan list." ulangi={ulangi} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-end gap-4 border-b border-border pb-4">
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
              {[
                { label: 'Monthly', nilai: false },
                { label: 'Yearly', nilai: true },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => padaGantiPeriode(p.nilai)}
                  aria-pressed={tahunan === p.nilai}
                  className={`rounded-sm px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                    tahunan === p.nilai
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {memuat && paket.length === 0 ? (
            <p className="py-10 text-sm text-muted-foreground">Loading plans…</p>
          ) : (
            <div className="grid lg:grid-cols-3">
              {paket.map((t) => (
                <KolomPaket key={t.code} paket={t} tahunan={tahunan} />
              ))}
            </div>
          )}

          <p className="mt-6 max-w-2xl text-sm text-muted-foreground">
            Posts, connected accounts, and the posters, carousels, and videos you create are counted
            across Pinterest, Instagram, and Threads together. Payment is via QRIS, and payment
            proof can be uploaded from the subscription page if the automatic notification is
            missed. All figures above are real prices, not examples.
          </p>
        </>
      )}
    </Bagian>
  )
}
